import { useState } from 'react';
import { Control, UseFieldArrayRemove, UseFieldArrayUpdate, useWatch } from 'react-hook-form';

import {
	AdaptiveSingleLineText,
	Button,
	Confirm,
	DynamicIconWrapper,
	Icons,
	Modal,
	SliderCheckbox,
	SubmissionButtons,
} from '@components/common';
import TextAvatar from '@components/common/TextAvatar';

import { useModal } from '@components/common/Modal/utils';

import { IOrganisationField } from '../interfaces';

import { MAX_450_LENGTH } from '@constants/validationMessages';
import { EUserMultipleOrganisationsFormFields, userFormTabNames } from '../constants';
import {
	DELETE_USER_ASSIGNED_TO_APPROVAL_UNITS_DISABLE_TOOLTIP,
	DELETE_USER_COMBINED_RESTRICTION_TOOLTIP,
	DELETE_USER_LAST_REMAINING_ADMIN_DISABLE_TOOLTIP,
	DELETE_USER_TOOLTIP,
	EDIT_USER_NON_ACTIVE_TOOLTIP,
	EDIT_USER_TOOLTIP,
	USER_STATUS_ASSIGNED_TO_APPROVAL_UNITS_DISABLE_TOOLTIP,
	USER_STATUS_COMBINED_RESTRICTION_DISABLE_TOOLTIP,
	USER_STATUS_DISABLE_TOOLTIP,
	USER_STATUS_LAST_REMAINING_ADMIN_DISABLE_TOOLTIP,
	USER_STATUS_NAME,
} from './constants';

import styles from './OrganisationAssignment.module.scss';

interface IOrganisationAssignmentProps {
	control: Control;
	isEditMode: boolean;
	isViewMode: boolean;
	setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	setCurrentTab: (tab: string) => void;
	openOrganisationAssignmentForm: () => void;
	setSelectedOrganisation: React.Dispatch<React.SetStateAction<IOrganisationField | null>>;
	remove: UseFieldArrayRemove;
	update: UseFieldArrayUpdate<any>;
	onSubmit: () => void;
}

const OrganisationAssignment = ({
	control,
	isViewMode,
	isEditMode,
	setIsEditMode,
	setCurrentTab,
	openOrganisationAssignmentForm,
	setSelectedOrganisation,
	remove,
	update,
	onSubmit,
}: IOrganisationAssignmentProps) => {
	const [isConfirmDelete, setIsConfirmDelete] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

	const isFieldsDisabled = isViewMode && !isEditMode;

	const { onClose } = useModal();

	const negativeHandler = () => {
		setIsConfirmDelete(false);
		setSelectedIndex(null);

		if (isFieldsDisabled) {
			setIsEditMode(true);

			return;
		}

		if (!isEditMode) {
			setCurrentTab(userFormTabNames.basicInformation);
		} else {
			onClose();
		}
	};

	const openConfirmDelete = (index: number) => {
		setIsConfirmDelete(true);
		setSelectedIndex(index);
	};

	const closeConfirmDelete = () => {
		setIsConfirmDelete(false);
		setSelectedIndex(null);
	};

	const watchedFields: IOrganisationField[] = useWatch({
		name: EUserMultipleOrganisationsFormFields.organisations,
		control,
	});

	const deleteOrganisationHandler = () => {
		if (selectedIndex !== null) {
			remove(selectedIndex);
		}

		closeConfirmDelete();
	};

	const positiveHandler = () => {
		if (isFieldsDisabled) {
			onClose();

			return;
		}

		onSubmit();
	};

	return (
		<div className={styles.wrapper}>
			<Modal
				isOpen={isConfirmDelete}
				onClose={closeConfirmDelete}
			>
				<Confirm
					onClose={closeConfirmDelete}
					onSubmit={deleteOrganisationHandler}
					question='Are you sure you want to delete user from this organisation?'
				>
					The user will lose their access to this organisation.
				</Confirm>
			</Modal>
			{!isFieldsDisabled && (
				<Button
					className={styles.button}
					icon={Icons.plus()}
					onClick={openOrganisationAssignmentForm}
					secondary
					text='Assign organisation'
				/>
			)}
			<div className={styles.organisationsList}>
				{watchedFields.map((field, index) => {
					const deleteOrganisationHandler = () => openConfirmDelete(index);
					const editOrganisationHandler = () => {
						openOrganisationAssignmentForm();
						setSelectedOrganisation(field);
					};

					const toggleSlider = (_id: string, checked: boolean) => {
						update(index, { ...watchedFields[index], isEnabled: checked });
					};

					const isLastRemainingAdmin = field.isLastAdmin;
					const isAssignedToApprovalUnits = field.isAssignedToApprovalUnits;
					const isCombinedRestriction = isLastRemainingAdmin && isAssignedToApprovalUnits;
					const isActionDisabled = isLastRemainingAdmin || isAssignedToApprovalUnits;

					return (
						<div
							key={field.organizationId}
							className={styles.organisationItem}
						>
							<div className={styles.organisationItemHeader}>
								<TextAvatar
									avatarId={field.avatar}
									fontSize='16px'
									fullName={field.title}
									id={field.organizationId}
									isLogoSquare
									isProfileLogo
									size='30px'
								/>
								<div className={styles.organisationItemTitle}>{field.title}</div>
								{!field.isActive && <span className={styles.organisationItemActive}>Inactive</span>}
							</div>
							<div className={styles.organisationItemInfo}>
								<AdaptiveSingleLineText
									className={styles.organisationItemRoles}
									maxWidth={MAX_450_LENGTH}
									text={field.roles.map(role => role?.label).filter(Boolean).join(', ')}
								/>
								<span className={styles.organisationItemJobTitle}>{field.jobTitle}</span>
							</div>
							<div className={styles.organisationItemControls}>
								<SliderCheckbox
									checked={field.isEnabled}
									disabled={isActionDisabled || isFieldsDisabled}
									id={USER_STATUS_NAME}
									label='User Status'
									name={USER_STATUS_NAME}
									onChange={toggleSlider}
									tooltipText={
										isCombinedRestriction
											? USER_STATUS_COMBINED_RESTRICTION_DISABLE_TOOLTIP
											: isAssignedToApprovalUnits
												? USER_STATUS_ASSIGNED_TO_APPROVAL_UNITS_DISABLE_TOOLTIP
												: isLastRemainingAdmin
													? USER_STATUS_LAST_REMAINING_ADMIN_DISABLE_TOOLTIP
													: USER_STATUS_DISABLE_TOOLTIP
									}
								/>
								<div className={styles.organisationItemActions}>
									{!isFieldsDisabled && (
										<>
											<DynamicIconWrapper
												icon={Icons.delete}
												isDisabled={isActionDisabled || isFieldsDisabled}
												onClick={deleteOrganisationHandler}
												tooltipLabel={
													isCombinedRestriction
														? DELETE_USER_COMBINED_RESTRICTION_TOOLTIP
														: isAssignedToApprovalUnits
															? DELETE_USER_ASSIGNED_TO_APPROVAL_UNITS_DISABLE_TOOLTIP
															: isLastRemainingAdmin
																? DELETE_USER_LAST_REMAINING_ADMIN_DISABLE_TOOLTIP
																: DELETE_USER_TOOLTIP
												}
											/>
											<DynamicIconWrapper
												icon={Icons.editIcon}
												isDisabled={!field.isActive}
												onClick={editOrganisationHandler}
												tooltipLabel={
													!field.isActive
														? EDIT_USER_NON_ACTIVE_TOOLTIP
														: EDIT_USER_TOOLTIP
												}
											/>
										</>
									)}
								</div>
							</div>
						</div>
					);
				})}
			</div>
			<SubmissionButtons
				className={styles.submitButtons}
				negativeHandler={negativeHandler}
				negativeLabel={isFieldsDisabled
					? 'Edit user'
					: isEditMode
						? 'Cancel'
						: 'Back'
				}
				positiveHandler={positiveHandler}
				positiveLabel={isFieldsDisabled ? 'Close' : 'Save'}
			/>
		</div>
	);
};

export default OrganisationAssignment;
